/* eslint-disable */
import { type FC, useState } from 'react';
import {
  DatePicker,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text
} from '@cencosud-cencommerce/paris-uikit';
import type { Value } from '@cencosud-cencommerce/paris-uikit/dist/molecules/DatePicker/DatePicker';
import type { GeneralDeliveryShippingOptionDto } from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto';
import { isSameDay } from '@/modules/shared/utils/isSameDay';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import {
  GtmGroupsData,
  ShippingEventInteractiveActions,
  ShippingEventInteractiveCategories,
  ShippingEventInteractiveTags
} from '@/modules/shared/utils/gtmEvents/types';
import { priceDtoToPriceString } from '@/modules/shared/mappers/priceDtoToPriceString';

interface Props {
  'data-testid'?: string;
  isOpen: boolean;
  onClose: VoidFunction;
  onConfirm: (date: Value) => void;
  scheduledOptions?: GeneralDeliveryShippingOptionDto[];
  gtmData?: GtmGroupsData;
}

const ModalDatePicker: FC<Props> = ({
  'data-testid': dataTestId = 'paris-modal-date-picker',
  isOpen,
  scheduledOptions,
  onClose,
  onConfirm,
  gtmData
}) => {
  const [selectedDate, setSelectedDate] = useState<Value | null>(null);
  const price = priceDtoToPriceString({
    currencyCode: 'CLP',
    centAmount: parseInt(
      String(scheduledOptions?.[0]?.shipmentCostTotal || '0')
    )
  });
  const options = scheduledOptions?.length ? scheduledOptions[0].options : [];
  const validDates: Date[] = [];
  const validDateRanges: { start: Date; end: Date }[] = [];

  /* not considering timeSlots */
  options?.forEach((opt) => {
    const { deliveryDateFrom, deliveryDateTo } = opt;
    const d1 = new Date(deliveryDateFrom);
    const d2 = new Date(deliveryDateTo);
    if (isSameDay(d1, d2)) {
      validDates.push(d1);
    } else {
      validDateRanges.push({ start: d1, end: d2 });
    }
  });

  const sendCloseDatePickerModalEvent = () => {
    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-accion': ShippingEventInteractiveActions.Click,
        'event-interactive-category':
          ShippingEventInteractiveCategories.Checkout,
        'event-interactive-tag':
          ShippingEventInteractiveTags.ClickCloseDatePickerModalButton,
        'event-interactive-grupo_despacho': `${gtmData?.groupNumber} - ${gtmData?.totalGroups}`
      }
    });
  };

  return (
    <Modal data-testid={dataTestId} isOpen={isOpen} size="sm">
      <ModalHeader
        onClose={() => {
          sendCloseDatePickerModalEvent();
          onClose();
        }}
        title="Despacho"
        icon={{
          name: 'calendar',
          fill: 'primary-500'
        }}
      />
      <ModalBody>
        <div className="co-items-center">
          <Text variant="body1" weight={600}>
            ¿Cuándo quieres recibir tu compra?
          </Text>
          <div>
            <div className="co-flex co-justify-center co-py-3">
              <DatePicker
                value={selectedDate}
                onChange={(date) => {
                  setSelectedDate(date);
                }}
                validDateRanges={validDateRanges}
                validDates={validDates}
              ></DatePicker>
            </div>
          </div>
          <div className="co-flex co-flex-col co-gap-2 co-p-3 co-border co-border-neutral-200 co-rounded-lg">
            <div className="co-flex co-justify-between">
              <Text variant="caption" weight={600}>
                Horario de entrega:
              </Text>
              <Text variant="caption" weight={600}>
                09:00 y 21:00 horas
              </Text>
            </div>
            <div className="co-flex co-justify-between">
              <Text variant="caption" weight={600}>
                Costo de despacho:
              </Text>
              {price && (
                <Text variant="caption" color="primary-500" weight={600}>
                  {price}
                </Text>
              )}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter
        primaryButton={{
          disabled: !selectedDate,
          label: 'Confirmar',
          onClick: () => {
            const concatenateUniqueTimeSlots = Array.from(
              new Set(
                options?.flatMap((item) => {
                  const timeSlots =
                    (
                      item as {
                        timeSlots: {
                          deliveryTimeStart: string;
                          deliveryTimeEnd: string;
                        }[];
                      }
                    )?.timeSlots ?? [];

                  return timeSlots.map(
                    (slot) =>
                      `${slot.deliveryTimeStart} a ${slot.deliveryTimeEnd}`
                  );
                })
              )
            ).join(' / ');

            const [{ shipmentCostTotal = '', originNodeAdmin = '' } = {}] =
              scheduledOptions ?? [];

            SendShippingEvents.sendEvent({
              event: 'baseEvent',
              extraFields: {
                'event-interactive-accion':
                  ShippingEventInteractiveActions.ModalConfirmDate,
                'event-interactive-tag': ShippingEventInteractiveTags.ReceiveOn,
                'event-interactive-despacho': concatenateUniqueTimeSlots,
                'event-interactive-grupo_despacho': `${gtmData?.groupNumber} - ${gtmData?.totalGroups}`,
                'event-interactive-vendedor': originNodeAdmin,
                'event-interactive-costo': shipmentCostTotal.toString()
              }
            });
            onConfirm(selectedDate);
            onClose();
          }
        }}
      ></ModalFooter>
    </Modal>
  );
};

export default ModalDatePicker;
